import { Loader } from "@design-system/Loader/Loader";
import { CheckIcon, MissingInfoIcon } from "@icons";
import type {
    DocVerificationField,
    DocVerificationStatus,
    MismatchReason,
} from "./documentVerificationStatus";

const FIELD_LABELS: Record<DocVerificationField, string> = {
    category: "category",
    account: "account",
    period: "period",
};

const MISMATCH_LABELS: Record<MismatchReason, string> = {
    mismatched_category: "Mismatched category",
    mismatched_account: "Mismatched account",
    mismatched_period: "Mismatched period",
    mismatched_period_start: "Mismatched period start",
    duplicate_period: "Duplicate period",
};

const MISMATCH_DESCRIPTIONS: Record<MismatchReason, string> = {
    mismatched_category:
        "This document's category doesn't match the categories expected for this task.",
    mismatched_account:
        "This document appears to belong to a different account than the one this task tracks.",
    mismatched_period:
        "This document's statement period falls outside the date range this task covers.",
    mismatched_period_start:
        "This document's statement start date doesn't line up with a single month in this task.",
    duplicate_period: "Another document already covers this statement period.",
};

const MISSING_FIELD_DESCRIPTIONS: Record<DocVerificationField, string> = {
    category: "We couldn't determine this document's category.",
    account:
        "We couldn't read an account number, so it can't be matched to the expected account.",
    period: "We couldn't read a statement period, so it can't be matched to a month.",
};

const MISSING_INFO_DESCRIPTION =
    "We couldn't extract enough information from this document to verify it. Open it to review.";

interface StatusDisplay {
    icon: React.ReactNode;
    label: string;
    className: string;
}

export function getVerificationDisplay(
    verification: DocVerificationStatus,
): StatusDisplay {
    switch (verification.kind) {
        case "verified":
            return {
                icon: <CheckIcon className="icon-color-gray-600" />,
                label: "Matched",
                className: "text-gray-600",
            };
        case "extracting":
            return {
                icon: <Loader inline />,
                label: "Extracting…",
                className: "text-gray-900",
            };
        case "mismatch":
            return {
                icon: <MissingInfoIcon className="icon-color-red-600" />,
                label: MISMATCH_LABELS[verification.reason],
                className: "text-red-600",
            };
        case "missing": {
            const label = verification.field
                ? `Missing ${FIELD_LABELS[verification.field]}`
                : "Missing info";
            return {
                icon: <MissingInfoIcon className="icon-color-red-600" />,
                label,
                className: "text-red-600",
            };
        }
    }
}

// A fuller, user-facing explanation of an error state, shown in the status
// tooltip so users understand *why* a document is unverified rather than just
// repeating the short cell label.
export function getVerificationDescription(
    verification: DocVerificationStatus,
): string | null {
    switch (verification.kind) {
        case "mismatch":
            return MISMATCH_DESCRIPTIONS[verification.reason];
        case "missing":
            return verification.field
                ? MISSING_FIELD_DESCRIPTIONS[verification.field]
                : MISSING_INFO_DESCRIPTION;
        default:
            return null;
    }
}
